import { Button, styled, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import LanguageIcon from '@mui/icons-material/Language';
import { usePlayerContext } from '../PlayerContext';
import { Dropdowns } from '../types';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

type SecondaryButtonsProps = {
  activeDropdown: Dropdowns;
  onToggleFullscreen(): void;
  onToggleSubtitles(): void;
  onToggleSpeed(): void;
  onToggleVariants(): void;
  onToggleAudio(): void;
};

const SecondaryButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  color: theme.palette.primary[20],
  borderColor: theme.palette.primary[20],
  '&:hover': {
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
    backgroundColor: 'transparent',
  },
}));

const isFullScreenEnabled = () => (document.fullscreenEnabled ? !!document.fullscreenElement : false);

export const SecondaryButtons: FC<SecondaryButtonsProps> = ({
  activeDropdown,
  onToggleFullscreen,
  onToggleSubtitles,
  onToggleSpeed,
  onToggleVariants,
  onToggleAudio,
}) => {
  const { t } = useTranslation();
  const disabled = (kind: Dropdowns) => activeDropdown !== 'hidden' && activeDropdown !== kind;
  const { player, getVideoOptions, getAudioOptions, getSubtitleOptions } = usePlayerContext();
  const variants = getVideoOptions();
  const audio = getAudioOptions();
  const subtitles = getSubtitleOptions();

  return (
    <>
      {audio.length > 1 && (
        <Tooltip title={t('player.controls.language.title')} placement="top">
          <SecondaryButton
            variant="outlined"
            onClick={event => {
              event.stopPropagation();
              onToggleAudio();
            }}
            disabled={disabled('languages')}
          >
            <LanguageIcon />
          </SecondaryButton>
        </Tooltip>
      )}
      {variants.length > 1 && (
        <Tooltip title={t('player.controls.quality.title')} placement="top">
          <SecondaryButton
            variant="outlined"
            onClick={event => {
              event.stopPropagation();
              onToggleVariants();
            }}
            disabled={disabled('variants')}
          >
            <TuneIcon />
          </SecondaryButton>
        </Tooltip>
      )}
      {subtitles.length > 0 && (
        <Tooltip title={t('player.controls.subtitles.title')} placement="top">
          <SecondaryButton
            variant="outlined"
            onClick={event => {
              event.stopPropagation();
              onToggleSubtitles();
            }}
            disabled={disabled('subtitles')}
          >
            <SubtitlesIcon />
          </SecondaryButton>
        </Tooltip>
      )}
      {!player?.isLive() && (
        <Tooltip title={t('player.controls.playbackSpeed.title')} placement="top">
          <SecondaryButton
            variant="outlined"
            onClick={event => {
              event.stopPropagation();
              onToggleSpeed();
            }}
            disabled={disabled('speed')}
          >
            <SlowMotionVideoIcon />
          </SecondaryButton>
        </Tooltip>
      )}
      <Tooltip title={t('player.controls.fullscreen')} placement="top">
        <SecondaryButton
          color="tertiary"
          variant="outlined"
          onClick={event => {
            event.stopPropagation();
            onToggleFullscreen();
          }}
          disabled={disabled('hidden')}
        >
          {isFullScreenEnabled() ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </SecondaryButton>
      </Tooltip>
    </>
  );
};
